import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { formatDate } from '../../utils/format';

/* ── Modale d'édition/création d'un contrat ───────────────────────────────────
   Extraite de HrContractsPage pour être réutilisée dans le hub People
   (vue maître-détail). Delete et Duplicate vivent désormais DANS la modale
   (footer gauche) : la ligne de table ne garde qu'un seul bouton ✎ Edit.

   Props :
     mode             'create' | 'edit'
     people           liste pour le select Person
     initial          contrat existant (edit) | null (create)
     lockedPersonId   si fourni, le select Person est verrouillé sur cet id
                      (création depuis une fiche personne)
     onCancel / onSubmit                        (obligatoires)
     onDelete / onDuplicate   (optionnels, mode edit) → rendent les boutons
     contracts        (optionnel) tous les contrats du tenant — sert à PROPOSER
                       la clôture du contrat précédent quand on en crée un
                       nouveau pour une personne qui en a déjà un ouvert
                       (décision Olivier 2026-08-20 : c'est le geste réel d'un
                       avenant). Case cochée par défaut, DÉCOCHABLE : la
                       décocher laisse volontairement deux contrats — cas de
                       l'avenant temporaire (taux réduit sur 3 mois, congé non
                       payé), que le calcul sait tenir depuis la règle unique
                       (backend hr_contract_rule : un seul contrat fait foi
                       par mois, le plus récemment commencé).
     movements        (optionnel) tous les mouvements du planning des effectifs
                       (payroll_simulation_movements) — sert à ALERTER si un
                       mouvement nominatif (fin de contrat / chgt de taux)
                       recouvre les dates du contrat en cours d'édition, pour
                       éviter le double compte (décision 2026-08-13 : pas de
                       lien en base entre contrat et mouvement, la garde reste
                       une alerte, pas un blocage).
     onGoToPlanning   (optionnel) callback → bascule vers Planification
                       personnel (le lien de l'alerte)
*/
export default function ContractEditModal({
  mode, people, initial, lockedPersonId,
  onCancel, onSubmit, onDelete, onDuplicate,
  defaultVacationDays = 25,
  movements, onGoToPlanning, contracts,
}) {
  const { t } = useTranslation();
  const p = useP();
  const [personId, setPersonId] = useState(initial?.person_id || lockedPersonId || people[0]?.id || '');
  const [startDate, setStartDate] = useState(initial?.start_date || `${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(initial?.end_date || '');
  const [title, setTitle] = useState(initial?.title || '');
  const [department, setDepartment] = useState(initial?.department || '');
  const [monthlySalary, setMonthlySalary] = useState(initial?.monthly_salary || '0');
  const [nbOfSalary, setNbOfSalary] = useState(initial?.nb_of_salary || 12);
  // Saisie en % (affichage métier) ; stockage en décimal (1.0 = 100%).
  const pctFromDecimal = (d) => (d == null || d === '') ? '100' : String(+(Number(d) * 100).toFixed(2));
  const [activityPct, setActivityPct] = useState(pctFromDecimal(initial?.activity_rate));
  const [vacationDays, setVacationDays] = useState(initial?.vacation_days ?? defaultVacationDays);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(false);  // delete / duplicate en cours
  const [err, setErr] = useState(null);

  const personLocked = mode === 'edit' || !!lockedPersonId;

  // Mouvements nominatifs (fin de contrat / chgt de taux) de CETTE personne
  // dont la période recouvre les dates saisies — alerte, ne bloque rien.
  const overlappingMoves = useMemo(() => {
    if (!movements?.length || !personId) return [];
    const cStart = startDate || null;
    const cEnd = endDate || null;
    return movements.filter((m) => {
      if (m.person_id !== personId) return false;
      if (!['rate_change', 'departure', 'salary_change'].includes(m.movement_type)) return false;
      if (!cStart) return false;
      const mStart = m.start_month, mEnd = m.end_month || null;
      const startsBeforeCEnds = !cEnd || mStart <= cEnd;
      const endsAfterCStarts = !mEnd || mEnd >= cStart;
      return startsBeforeCEnds && endsAfterCStarts;
    });
  }, [movements, personId, startDate, endDate]);
  /* Contrats de la personne encore ouverts à la date de début du nouveau :
     ils seraient recouverts. On propose de les clore la VEILLE — l'avenant
     remplace le contrat précédent, c'est le geste courant. */
  const dayBefore = useMemo(() => {
    if (!startDate) return '';
    const d = new Date(startDate);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, [startDate]);
  const toClose = useMemo(() => {
    if (mode !== 'create' || !contracts?.length || !personId || !startDate) return [];
    return contracts.filter((c) => c.person_id === personId
      && c.start_date && c.start_date < startDate
      && (!c.end_date || c.end_date >= startDate));
  }, [mode, contracts, personId, startDate]);
  const [closePrev, setClosePrev] = useState(true);

  const moveLabel = (m) => (m.movement_type === 'departure' ? p('Fin de contrat')
    : m.movement_type === 'salary_change' ? p('Modification du salaire') : p('Chgt de taux'));

  async function submit() {
    if (!personId || !startDate) {
      setErr(p('Le collaborateur et la date de début sont requis.'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    const payload = {
      start_date: startDate,
      end_date: endDate || null,
      title: title || null,
      department: department || null,
      monthly_salary: monthlySalary,
      nb_of_salary: Number(nbOfSalary),
      activity_rate: String(Number(activityPct || 0) / 100),
      vacation_days: Number(vacationDays),
    };
    if (mode === 'create') payload.person_id = personId;
    // Le parent applique la clôture (PATCH) avant de rafraîchir : la modale
    // dit CE QU'IL FAUT FAIRE, elle n'écrit pas elle-même.
    const closing = (closePrev && toClose.length && dayBefore)
      ? toClose.map((c) => ({ id: c.id, end_date: dayBefore })) : [];
    const ok = await onSubmit(payload, closing);
    if (!ok) setSubmitting(false);
  }

  async function handleDelete() {
    if (!onDelete) return;
    setBusy(true);
    setErr(null);
    const ok = await onDelete();
    if (!ok) setBusy(false);
  }

  async function handleDuplicate() {
    if (!onDuplicate) return;
    setBusy(true);
    setErr(null);
    const ok = await onDuplicate();
    if (!ok) setBusy(false);
  }

  const anyBusy = submitting || busy;

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal hr-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{mode === 'create' ? p('Ajouter un contrat') : p('Modifier le contrat')}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* Le collaborateur a déjà un contrat couvrant cette date : on
              propose de le clore la veille (l'avenant remplace le précédent).
              Décocher = deux contrats assumés — avenant temporaire, que le
              calcul tient (un seul contrat fait foi par mois). */}
          {toClose.length > 0 && (
            <div className="hr-modal__warn" style={{ gridColumn: '1 / -1' }}>
              <span className="hr-modal__warn-icon" aria-hidden>⚠</span>
              <div>
                <div className="hr-modal__warn-title">
                  {toClose.length > 1
                    ? p('Ce collaborateur a déjà des contrats couvrant cette date :')
                    : p('Ce collaborateur a déjà un contrat couvrant cette date :')}
                </div>
                <ul className="hr-modal__warn-list">
                  {toClose.map((c) => (
                    <li key={c.id}>
                      {c.title || p('Contrat')} — {p('dès')} {formatDate(c.start_date)}
                      {c.end_date ? ` → ${formatDate(c.end_date)}` : ` → ${p('sans fin')}`}
                    </li>
                  ))}
                </ul>
                <label className="hr-modal__warn-check">
                  <input type="checkbox" checked={closePrev} onChange={(e) => setClosePrev(e.target.checked)} />
                  {toClose.length > 1
                    ? `${p('Clore ces contrats au')} ${formatDate(dayBefore)}`
                    : `${p('Clore le contrat précédent au')} ${formatDate(dayBefore)}`}
                </label>
                {!closePrev && (
                  <div className="hr-modal__warn-note">
                    {p('Les deux contrats se recouvriront — le plus récent fera foi sur la période (avenant temporaire).')}
                  </div>
                )}
              </div>
            </div>
          )}
          {overlappingMoves.length > 0 && (
            <div className="hr-modal__warn" style={{ gridColumn: '1 / -1' }}>
              <span className="hr-modal__warn-icon" aria-hidden>⚠</span>
              <div>
                <div className="hr-modal__warn-title">
                  {p('Attention, il y a un mouvement planifié pour ce collaborateur sur cette période :')}
                </div>
                <ul className="hr-modal__warn-list">
                  {overlappingMoves.map((m) => (
                    <li key={m.id}>
                      {moveLabel(m)} — {p('dès')} {formatDate(m.start_month)}
                      {m.end_month ? ` → ${formatDate(m.end_month)}` : ''}
                    </li>
                  ))}
                </ul>
                {onGoToPlanning && (
                  <button type="button" className="hr-modal__warn-link" onClick={onGoToPlanning}>
                    {p('Voir dans Planification personnel (et supprimer si besoin) →')}
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="hr-modal__field" style={{ gridColumn: '1 / -1' }}>
            <label className="hr-modal__label">{p('Collaborateur *')}</label>
            <select
              className="hr-modal__input"
              value={personId}
              onChange={e => setPersonId(e.target.value)}
              disabled={personLocked}
            >
              {people.map(person => <option key={person.id} value={person.id}>{person.last_name} {person.first_name}</option>)}
            </select>
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Date de début *')}</label>
            <input className="hr-modal__input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Date de fin ')}<span className="hr-modal__hint">{p('(vide = en cours)')}</span></label>
            <input className="hr-modal__input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Titre')}</label>
            <input className="hr-modal__input" type="text" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{t('phrases:p_3d7c87c2af')}</label>
            <input className="hr-modal__input" type="text" value={department} onChange={e => setDepartment(e.target.value)} />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Salaire mensuel (CHF)')}</label>
            <input className="hr-modal__input" type="number" step="1" value={monthlySalary} onChange={e => setMonthlySalary(e.target.value)} />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Nb de salaires')}</label>
            <select className="hr-modal__input" value={nbOfSalary} onChange={e => setNbOfSalary(e.target.value)}>
              <option value={12}>12</option>
              <option value={13}>13</option>
              <option value={14}>14</option>
            </select>
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{t('phrases:p_a48842269e')}<span className="hr-modal__hint">(%)</span></label>
            <input className="hr-modal__input" type="number" step="1" min="1" max="150" value={activityPct} onChange={e => setActivityPct(e.target.value)} placeholder="100" />
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Jours de vacances')}</label>
            <input className="hr-modal__input" type="number" step="1" min="0" max="50" value={vacationDays} onChange={e => setVacationDays(e.target.value)} />
          </div>
          {err &&<div className="hr-modal__error" style={{ gridColumn: '1 / -1' }}>{err}</div>}
        </div>
        <div className="hr-modal__footer">
          {mode === 'edit' && (onDelete || onDuplicate) && (
            <div style={{ display: 'flex', gap: 8, marginRight: 'auto' }}>
              {onDelete && (
                <button className="hr-modal__btn hr-modal__btn--danger" onClick={handleDelete} disabled={anyBusy}>
                  {t('common:actions.delete')}
                </button>
              )}
              {onDuplicate && (
                <button className="hr-modal__btn" onClick={handleDuplicate} disabled={anyBusy}>
                  {t('common:actions.duplicate')}
                </button>
              )}
            </div>
          )}
          <button className="hr-modal__btn" onClick={onCancel} disabled={anyBusy}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary" onClick={submit} disabled={anyBusy}>
            {submitting ? p('Enregistrement…') : (mode === 'create' ? p('Ajouter un contrat') : t('common:actions.save'))}
          </button>
        </div>
      </div>
    </div>
  );
}
